import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAPI } from '../../Contexts/APIContext'

/**
 * f_id -> admin display name, for turning an ingest request's raw
 * `requested_by` (an admin f_id - store.js INSERT ... requested_by, never a
 * name or email, see content-admin/ingest's own API-PAYLOAD.md) into a name
 * a person recognizes.
 *
 * GET /admin/user (admin-service-api route_1.0/routes/admin_manage/admin-
 * user/get-admin-user.js) is the only existing endpoint that can resolve
 * this, and it needs PERMISSION_ADMIN + PERMISSION_ACCESS_ALL - a real admin-
 * management permission, a higher bar than plain ingest access
 * (organization_internal.is_internal + is_admin + artist/song permissions).
 * Most catalogue-team users will 403 calling it, which is expected and
 * silent here (retry: false, suppressGlobalError) - every caller must treat
 * an empty map as "fall back to the raw id", never as an error to show.
 */
export default function useAdminUserNames() {
  const { getAdminUsers } = useAPI()
  const query = useQuery({
    queryKey: ['admin-users'],
    queryFn: getAdminUsers,
    retry: false,
    staleTime: 5 * 60 * 1000,
    meta: { suppressGlobalError: true },
  })
  return useMemo(() => {
    const map = new Map()
    for (const user of (query.data && query.data.admin_users) || []) {
      if (user && user.f_id) map.set(user.f_id, user.name || null)
    }
    return map
  }, [query.data])
}
